import { useLayoutEffect, useState, useMemo, useCallback } from "react";
import { UserPermissions } from "src/app/providers/permissions/permissions";
import {
  AdminRoleAction,
  canPerformAdminRoleAction,
} from "src/app/providers/permissions/admin-role-policy";
import { getPrimaryMHCRoleLabel } from "src/app/providers/permissions/mhc-roles";

type Permission = string;

export function usePermission() {
  const [userPermissions, setUserPermissions] = useState<Permission[]>([]);
  const [userRoles, setUserRoles] = useState<string[]>([]);

  useLayoutEffect(() => {
    const retrieveUserPermissions = async () => {
      const permissions = localStorage.getItem("userMHCPermissions");
      setUserPermissions(JSON.parse(permissions || "[]"));
      const roles = localStorage.getItem("userMHCRoles");
      setUserRoles(JSON.parse(roles || "[]"));
    };

    retrieveUserPermissions();
  }, []);

  function hasPermission(to: Permission | Permission[]) {
    if (Array.isArray(to)) {
      return to.some((p) => userPermissions.includes(p));
    }
    return userPermissions.includes(to);
  }

  // Check if user has ONLY the Karibu Utility access permission (no other permissions)
  const hasOnlyKaribuUtilityAccess = useMemo(() => {
    if (userPermissions.length === 0) return false;

    const karibuPermissions = [
      UserPermissions.KARIBU_UTILITY.ACCESS,
      UserPermissions.KARIBU_UTILITY.REPROCESS,
      UserPermissions.PAYMENTS.CREATE,
      UserPermissions.PAYMENTS.VIEW,
      UserPermissions.PAYMENTS.EDIT,
    ];

    // Check if all user permissions are Karibu Utility permissions
    const hasOnlyKaribuPermissions = userPermissions.every((p) =>
      karibuPermissions.includes(p),
    );

    // Must have the ACCESS permission
    const hasAccessPermission = userPermissions.includes(
      UserPermissions.KARIBU_UTILITY.ACCESS,
    );

    return hasOnlyKaribuPermissions && hasAccessPermission;
  }, [userPermissions]);

  // Check if user has Karibu Utility access (but may have other permissions too)
  const hasKaribuUtilityAccess = useMemo(() => {
    return userPermissions.includes(UserPermissions.KARIBU_UTILITY.ACCESS);
  }, [userPermissions]);

  // Check if user can reprocess BODs
  const canReprocessBod = useMemo(() => {
    return userPermissions.includes(UserPermissions.KARIBU_UTILITY.REPROCESS);
  }, [userPermissions]);

  const canPerformRoleAction = useCallback(
    (action: AdminRoleAction) => {
      return canPerformAdminRoleAction(userRoles, action);
    },
    [userRoles],
  );

  const primaryRoleLabel = useMemo(
    () => getPrimaryMHCRoleLabel(userRoles),
    [userRoles],
  );

  return {
    userPermissions,
    hasPermission,
    canPerformRoleAction,
    primaryRoleLabel,
    hasOnlyKaribuUtilityAccess,
    hasKaribuUtilityAccess,
    canReprocessBod,
  };
}
